import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";

const firstName = (n) => String(n || "").trim().split(" ")[0] || "This client";
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
const classes = (n) => (n === 1 ? "1 class" : `${n} classes`);

// Offers the trainer the option to spend a client's pack credits on the
// upcoming classes of a recurring series that aren't covered yet. Used after a
// pack sale (Packages page) and from the series card in the client drawer.
export function PackCoverageDialog({ open, onOpenChange, clientId, seriesId, onDone }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [warning, setWarning] = useState(null);

  const load = useCallback(async () => {
    const { data: d } = await api.get(`/clients/${clientId}/pack-coverage`);
    setData(d);
    return d;
  }, [clientId]);

  useEffect(() => {
    if (!open || !clientId) return;
    setWarning(null);
    setData(null);
    load().catch(() => onOpenChange(false));
  }, [open, clientId, load, onOpenChange]);

  const target = data
    ? seriesId
      ? data.series.find((s) => s.series_id === seriesId)
      : data.series.find((s) => s.eligible_count > 0)
    : null;

  const credits = data?.credits ?? 0;
  const eligible = target?.eligible_count ?? 0;
  const willCover = Math.min(credits, eligible);
  const name = firstName(data?.client_name);

  const cover = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/recurring/${target.series_id}/cover-with-pack`, {
        expected_credits: credits,
      });
      onOpenChange(false);
      if (res.covered === 0) {
        toast.message("Nothing to cover — those classes are already covered or no credits are left.");
      } else {
        toast.success(
          `${classes(res.covered)} covered by ${firstName(res.client_name)}'s pack. ${plural(
            res.credits_left,
            "credit"
          )} left.`
        );
      }
      if (onDone) await onDone();
    } catch (e) {
      if (e?.response?.status === 409) {
        setWarning(e.response.data.detail);
        await load().catch(() => {});
      } else {
        toast.error(e?.response?.data?.detail || "Could not use the pack credits");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent
        className="bg-[#FFFFFF] border-[#E8E1D6] text-[#2A2623] max-w-[92vw] sm:max-w-md"
        data-testid="pack-coverage-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase text-[#2A2623]">
            Use pack credits?
          </DialogTitle>
          <DialogDescription className="text-[#2A2623] text-sm" data-testid="pack-coverage-question">
            {!data
              ? "Checking this client's upcoming classes…"
              : eligible === 0
              ? `${name} has no upcoming series classes that need covering.`
              : `${name} has ${eligible} upcoming series ${
                  eligible === 1 ? "class" : "classes"
                } that aren't covered by a pack. Use pack credits for them?`}
          </DialogDescription>
        </DialogHeader>

        {data && eligible > 0 && (
          <div className="rounded-md border border-[#E8E1D6] bg-[#FAF7F2] p-3 text-sm space-y-1">
            <p className="text-[#2A2623]" data-testid="pack-coverage-credits">
              {plural(credits, "credit")} available
            </p>
            <p className="text-[#0F6B6B] font-semibold" data-testid="pack-coverage-will-cover">
              {plural(willCover, "week")} will be covered
            </p>
            {eligible > willCover && (
              <p className="text-[#6B635C] text-xs" data-testid="pack-coverage-leftover">
                The other {plural(eligible - willCover, "week")} stay uncovered and are invoiced when
                you mark them complete.
              </p>
            )}
          </div>
        )}

        {warning && (
          <p
            className="text-[#7A5E18] text-xs rounded border border-[#ECDCA6] bg-[#FBF4DD] p-2"
            data-testid="pack-coverage-warning"
          >
            {warning}
          </p>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="pack-coverage-not-now"
            className="border-[#E8E1D6] bg-transparent hover:bg-[#FAF7F2]"
          >
            Not now
          </Button>
          <Button
            onClick={cover}
            disabled={busy || !target || willCover === 0}
            data-testid="pack-coverage-confirm"
            className="bg-[#0F6B6B] text-[#FFFFFF] hover:bg-[#0B5757] font-semibold"
          >
            {busy ? "Covering…" : "Cover them"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
